import { useMemo, useState } from "react";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import TextBox from "../../../../components/form/TextBox";
import { JobCancelReasonShare } from "../models/JobReportModel";
import "./CancelReasonsCard.css";

interface IProps {
    items?: JobCancelReasonShare[]
    selected: string[]
    shownJobs?: number
    onSelectedChange: (keys: string[]) => void
}

// One color per reason, in the order the HUB sends them (largest first). The wordings under
// "Other reasons" are all gray so they never read like reasons of their own.
const BAR_COLORS = [
    "var(--bs-primary)", "var(--bs-danger)", "var(--bs-warning)", "var(--bs-success)", "var(--bs-info)",
    "#7239EA", "#F1416C", "#43CED7", "#E4A6F5", "#FF8F3F", "#3E97FF", "#2ECC71"
];
const OTHER_COLOR = "var(--bs-gray-500)";

// The Canceled Jobs segment's breakdown by reason. Reason records, frequently typed notes and
// "Other reasons" come from the HUB already grouped (DataService.JobsReports.CancelReasons).
// Clicking a reason toggles it in `selected`, and the report filters its jobs table by the
// selected reasons; "Other reasons" opens up instead, listing every wording it sums.
export default function CancelReasonsCard(props: IProps) {

    const [otherOpen, otherOpenSet] = useState<boolean>(false);
    const [otherSearch, otherSearchSet] = useState<string>("");

    const otherItems = useMemo(() => {
        const q = otherSearch.trim().toLowerCase();
        const all = props.items?.find(f => f.isOther)?.items ?? [];
        return all.filter(f => !q || (f.name ?? "").toLowerCase().includes(q));
    }, [props.items, otherSearch]);

    const items = props.items ?? [];
    const other = items.find(f => f.isOther);

    if (items.length == 0) return <></>;

    const total = items.reduce((sum, f) => sum + f.count, 0);
    const max = Math.max(...items.map(f => f.count));
    const otherMax = Math.max(1, ...(other?.items ?? []).map(f => f.count));
    const otherSelected = (other?.items ?? []).filter(f => props.selected.includes(f.key)).length;

    const toggle = (key: string) => {
        props.onSelectedChange(props.selected.includes(key)
            ? props.selected.filter(f => f != key)
            : [...props.selected, key]);
    }

    // A plain render function rather than a component, so the rows are not remounted on every render.
    const reasonRow = (p: { item: JobCancelReasonShare, color: string, max: number, compact?: boolean }) => {
        const selected = props.selected.includes(p.item.key);
        const isOther = !!p.item.isOther;
        return (
            <div key={p.item.key} className={`cancel-reason-row ${selected ? "selected" : ""} ${isOther && otherOpen ? "open" : ""} ${p.compact ? "compact" : ""}`}
                title={isOther ? undefined : p.item.name}
                onClick={() => isOther ? otherOpenSet(o => !o) : toggle(p.item.key)}>
                <FlexRow>
                    <span className="cancel-reason-mark me-2" style={{ color: p.color }}>
                        <Icon icon={selected ? "circle-check" : "circle"} type="solid" size={selected ? 13 : 9} />
                    </span>
                    <span className={`text-truncate ${p.compact ? "fs-8" : "fs-7"} ${isOther ? "text-gray-700" : "text-gray-800"} fw-semibold`}>
                        {p.item.name}
                    </span>
                    {
                        isOther &&
                        <span className="text-muted fs-8 ms-2 text-nowrap">
                            {p.item.items?.length} notes{otherSelected > 0 ? ` · ${otherSelected} selected` : ""}
                        </span>
                    }
                    <span className="ms-auto ps-3 fw-bold fs-7 text-gray-900 text-nowrap">{p.item.count.toLocaleString()}</span>
                    {
                        !p.compact &&
                        <span className="text-muted fs-8 text-end text-nowrap" style={{ width: 38 }}>{p.item.percent}%</span>
                    }
                    {
                        isOther &&
                        <Icon icon={otherOpen ? "chevron-up" : "chevron-down"} size={11} className="ms-2 text-gray-500" />
                    }
                </FlexRow>
                <div className="cancel-reason-track mt-2">
                    <div className="cancel-reason-fill" style={{ width: `${Math.max(1, (p.item.count * 100) / p.max)}%`, backgroundColor: p.color }} />
                </div>
            </div>
        )
    }

    return (
        <div className="card mt-2">
            <div className="card-body py-5">

                <FlexRow className="mb-4">
                    <div>
                        <div className="fw-bold fs-5 text-gray-900">Cancel Reasons</div>
                        <div className="text-muted fs-8 mt-1">
                            {total.toLocaleString()} canceled job{total == 1 ? "" : "s"} &middot; click a reason to filter the table
                        </div>
                    </div>
                    {
                        props.selected.length > 0 &&
                        <FlexRow className="ms-auto">
                            <span className="text-gray-700 fs-7 me-3">
                                Showing <b>{(props.shownJobs ?? 0).toLocaleString()}</b> of {total.toLocaleString()} jobs
                            </span>
                            <button type="button" className="btn btn-sm btn-light-primary py-1 px-3"
                                onClick={() => props.onSelectedChange([])}>
                                <Icon icon="times" size={11} className="me-1" />
                                Clear
                            </button>
                        </FlexRow>
                    }
                </FlexRow>

                <div className="cancel-reasons-grid">
                    {
                        items.map((item, k) =>
                            reasonRow({ item, max, color: item.isOther ? OTHER_COLOR : BAR_COLORS[k % BAR_COLORS.length] })
                        )
                    }
                </div>

                {
                    other && otherOpen &&
                    <div className="cancel-reasons-other mt-4 pt-4">
                        <FlexRow className="mb-3">
                            <span className="fw-bold fs-7 text-gray-800">
                                Other reasons &middot; {other.items?.length.toLocaleString()} different notes
                            </span>
                            <div className="ms-auto" style={{ width: 240 }}>
                                <TextBox small solid clear block noMarginBottom placeholder="Search notes..." icon="search"
                                    value={otherSearch} onTextChanges={t => otherSearchSet(t)} />
                            </div>
                        </FlexRow>
                        <div className="cancel-reasons-other-list">
                            {
                                otherItems.map(item =>
                                    reasonRow({ item, max: otherMax, color: OTHER_COLOR, compact: true })
                                )
                            }
                            {
                                otherItems.length == 0 &&
                                <div className="text-muted fs-7 py-3">No notes match "{otherSearch}"</div>
                            }
                        </div>
                    </div>
                }

            </div>
        </div>
    )
}
